import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useAuth } from "../App";
import { Input } from "../components/ui/input";
import {
  BookOpen, Search, ChevronRight, X,
  CheckCircle, Star, ArrowLeft, HelpCircle, GripVertical
} from "lucide-react";

// ===== MODULE IMAGES =====
const moduleImages = {
  "Mi Dashboard": "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?w=600&q=60",
  "Dashboard Ejecutivo": "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?w=600&q=60",
  "Riesgos Generales": "https://images.pexels.com/photos/7731333/pexels-photo-7731333.jpeg?auto=compress&w=600",
  "Cotizaciones": "https://images.pexels.com/photos/7734672/pexels-photo-7734672.jpeg?auto=compress&w=600",
  "Gestión Humana": "https://images.pexels.com/photos/36733300/pexels-photo-36733300.jpeg?auto=compress&w=600",
  "Riesgos de Personas": "https://images.pexels.com/photos/7731333/pexels-photo-7731333.jpeg?auto=compress&w=600",
  "Informaciones Generales": "https://images.pexels.com/photos/6326042/pexels-photo-6326042.jpeg?auto=compress&w=600",
  "Mensajería": "https://images.pexels.com/photos/6868457/pexels-photo-6868457.jpeg?auto=compress&w=600",
  "Mesa de Ayuda": "https://images.pexels.com/photos/7709290/pexels-photo-7709290.jpeg?auto=compress&w=600",
  "WhatsApp": "https://images.unsplash.com/photo-1644035772775-8e26685ad2f0?w=600&q=60",
  "Finanzas y Contabilidad": "https://images.pexels.com/photos/6779567/pexels-photo-6779567.jpeg?auto=compress&w=600",
  "Control Visitantes": "https://images.unsplash.com/photo-1759143545924-beb85b33c0f1?w=600&q=60",
  "Requisiciones": "https://images.unsplash.com/photo-1579808352667-5db8c02598ce?w=600&q=60",
  "Artículos Marketing": "https://images.pexels.com/photos/6326042/pexels-photo-6326042.jpeg?auto=compress&w=600",
  "Modulo de encuesta": "https://images.unsplash.com/photo-1587882242636-596ab6349e69?w=600&q=60",
  "Estadísticas": "https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&w=600",
  "Convertidor de Documentos": "https://images.pexels.com/photos/7731333/pexels-photo-7731333.jpeg?auto=compress&w=600",
  "Publicidades": "https://images.pexels.com/photos/6326041/pexels-photo-6326041.jpeg?auto=compress&w=600",
  "Capacitaciones": "https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?w=600&q=60",
  "Exámenes": "https://images.unsplash.com/photo-1640163561346-7778a2edf353?w=600&q=60",
  "Reportes": "https://images.pexels.com/photos/97080/pexels-photo-97080.jpeg?auto=compress&w=600",
  "Configuración": "https://images.pexels.com/photos/3785930/pexels-photo-3785930.jpeg?auto=compress&w=600",
};

// ===== MANUAL CONTENT DATABASE =====
const manualDB = {
  "Mi Dashboard": {
    desc: "Panel personalizado con sus tareas, pendientes y actividad reciente",
    steps: [
      { t: "Vista general", d: "Al ingresar verá un resumen de su actividad: tareas pendientes, solicitudes en proceso y accesos rápidos a sus módulos más usados." },
      { t: "Actividad reciente", d: "En la parte inferior encontrará un historial de sus acciones recientes en la plataforma." },
    ],
    tips: ["Este panel se actualiza en tiempo real con sus tareas y notificaciones."],
    submenus: {}
  },
  "Dashboard Ejecutivo": {
    desc: "KPIs generales de la organización para la alta gerencia",
    steps: [
      { t: "Acceder al dashboard", d: "Navegue a Dashboard Ejecutivo > Dashboard KPI para ver métricas de rendimiento globales." },
      { t: "Indicadores clave", d: "Las tarjetas muestran: comparaciones realizadas, informes generados, documentos procesados y eficiencia operativa." },
      { t: "Gráficos de tendencia", d: "Los gráficos muestran la evolución temporal de las métricas principales." },
    ],
    tips: ["Las tasas de cambio USD y EUR se actualizan automáticamente."],
    submenus: {
      "Dashboard KPI": {
        desc: "Visualización de indicadores clave de rendimiento empresarial",
        img: "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&w=600",
        steps: [
          { t: "Métricas principales", d: "Visualice los KPIs de la empresa: comparaciones, informes, documentos y tiempo ahorrado con IA." },
          { t: "Filtrar por período", d: "Use los filtros de fecha para analizar métricas en períodos específicos." },
        ]
      }
    }
  },
  "Riesgos Generales": {
    desc: "Gestión de pólizas, comparaciones y control de renovaciones",
    steps: [
      { t: "Acceder al módulo", d: "Desde el menú lateral, seleccione 'Riesgos Generales' para ver todas las opciones disponibles." },
    ],
    tips: ["Use el Dashboard para una vista general del estado de las pólizas."],
    submenus: {
      "Dashboard": {
        desc: "Panel con estadísticas de pólizas activas y próximas a vencer",
        img: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?w=600&q=60",
        steps: [
          { t: "Vista general", d: "Panel con estadísticas de pólizas activas, próximas a vencer y renovadas." },
        ]
      },
      "Nueva Comparación": {
        desc: "Crear análisis comparativo de pólizas usando IA",
        img: "https://images.pexels.com/photos/7734672/pexels-photo-7734672.jpeg?auto=compress&w=600",
        steps: [
          { t: "Crear comparación", d: "Haga clic en 'Nueva Comparación' e ingrese: Nombre del archivo y suba el documento PDF o Excel." },
          { t: "Procesamiento IA", d: "La IA extraerá automáticamente datos del documento: coberturas, primas, deducibles y beneficios." },
          { t: "Ver resultados", d: "Una vez procesado, verá los datos extraídos organizados en una tabla comparativa." },
        ]
      },
      "Cobertura de Pólizas": {
        desc: "Consultar coberturas vigentes con filtros por estado",
        steps: [
          { t: "Consultar coberturas", d: "Vea el listado de pólizas con sus coberturas vigentes, montos y fechas de vencimiento." },
          { t: "Filtrar por estado", d: "Use los filtros para ver pólizas: Vigentes, Próximas a vencer o Vencidas." },
        ]
      },
      "Informes": {
        desc: "Generación de reportes descargables de riesgos",
        steps: [
          { t: "Generar informe", d: "Seleccione el tipo de informe y el período. El sistema generará un reporte descargable." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas de documentos para estandarizar informes",
        steps: [
          { t: "Gestionar plantillas", d: "Cree y edite plantillas de documentos para estandarizar los informes de riesgos generales." },
        ]
      },
      "Plantillas Aseguradoras": {
        desc: "Plantillas específicas por aseguradora",
        steps: [
          { t: "Configurar plantillas", d: "Defina plantillas específicas por aseguradora para facilitar la comparación de propuestas." },
        ]
      },
      "Control de Llamadas": {
        desc: "Registro y seguimiento de llamadas a clientes o aseguradoras",
        steps: [
          { t: "Registrar llamada", d: "Registre llamadas realizadas a clientes o aseguradoras con: fecha, hora, contacto, asunto y resultado." },
          { t: "Historial", d: "Consulte el historial de llamadas con filtros por fecha, cliente y agente." },
        ]
      },
      "Control de Renovaciones": {
        desc: "Seguimiento de pólizas próximas a vencer",
        steps: [
          { t: "Ver renovaciones", d: "Listado de pólizas con fecha de vencimiento, estado y días restantes para la renovación." },
          { t: "Filtrar por estado", d: "Filtre por: Próximas a vencer (30, 60, 90 días), Vencidas, Renovadas." },
          { t: "Gestionar renovación", d: "Haga clic en una póliza para iniciar el proceso de renovación y dar seguimiento." },
        ]
      },
    }
  },
  "Cotizaciones": {
    desc: "Comparación inteligente de propuestas de seguros con IA",
    steps: [
      { t: "Acceder", d: "Navegue a 'Cotizaciones' desde el menú lateral." },
    ],
    tips: ["La IA puede leer PDFs y Excel directamente.", "Las coberturas se auto-alimentan desde los documentos."],
    submenus: {
      "Dashboard": {
        desc: "Panel principal de cotizaciones con todas las funciones",
        img: "https://images.pexels.com/photos/7734672/pexels-photo-7734672.jpeg?auto=compress&w=600",
        steps: [
          { t: "Crear nueva cotización", d: "Haga clic en '+ Nueva Cotización'. Complete: Título, Cliente, Departamento (Salud Local, Internacional o Riesgos Generales), Ramo y datos del cliente." },
          { t: "Cargar documentos", d: "Dentro de la cotización, use 'Cargar Documento' para subir PDFs o Excel. Puede arrastrar y soltar (Drag & Drop). La IA extraerá automáticamente toda la información." },
          { t: "Analizar con IA", d: "Con 2+ propuestas, haga clic en 'Analizar con IA'. Generará: Ranking con puntajes, Comparativa de Coberturas, Primas y Recomendación profesional." },
          { t: "Pestaña Comparativo", d: "Tarjetas con: Prima Anual, Deducible, Coaseguro, Tope de Cobertura y coberturas expandibles (+N más)." },
          { t: "Pestaña Análisis IA", d: "Dictamen IA con recomendación global, Mejor Opción/Precio/Coberturas y Ranking detallado." },
          { t: "Pestaña Gráficos", d: "Gráficos de barras, radar multicriteria y tabla comparativa agrupada por aseguradora y plan." },
          { t: "Exportar Excel", d: "Descargue comparativo profesional con logo, colores institucionales y sin líneas de celda." },
          { t: "Personalizar Cotización", d: "Genere informe ejecutivo en PDF o Word con datos del cliente y formato profesional." },
          { t: "Compartir", d: "Comparta la cotización con otros usuarios de la plataforma." },
          { t: "Configuración (Admin)", d: "Pestaña 'Configuración': gestione Aseguradoras, Coberturas, Deducibles y Planes. Edite inline con doble clic." },
        ]
      }
    }
  },
  "Gestión Humana": {
    desc: "Administración de empleados, vacaciones, reclutamiento y nómina",
    steps: [
      { t: "Acceder", d: "Desde el menú lateral, seleccione 'Gestión Humana' para ver todos los submódulos." },
    ],
    tips: ["Los cumpleaños del mes se muestran automáticamente.", "Las tardanzas generan alertas automáticas."],
    submenus: {
      "Empleados": {
        desc: "Gestión completa de la ficha de colaboradores",
        img: "https://images.pexels.com/photos/36733300/pexels-photo-36733300.jpeg?auto=compress&w=600",
        steps: [
          { t: "Ver empleados", d: "Listado completo de colaboradores con foto, nombre, departamento, cargo y estado." },
          { t: "Agregar empleado", d: "Haga clic en '+ Nuevo Empleado' y complete el formulario con datos personales, laborales y documentos." },
          { t: "Editar empleado", d: "Haga clic en un empleado para ver/editar su ficha completa: datos, documentos, historial." },
          { t: "Buscar", d: "Use el buscador para encontrar empleados por nombre, cédula o departamento." },
        ]
      },
      "Solicitudes": {
        desc: "Creación y aprobación de solicitudes de RRHH",
        steps: [
          { t: "Crear solicitud", d: "Los empleados pueden crear solicitudes de: cartas de trabajo, constancias, avances de prestaciones y otros documentos." },
          { t: "Ver mis solicitudes", d: "Consulte el estado de sus solicitudes: Pendiente, Aprobada, Rechazada, Completada." },
          { t: "Aprobar solicitudes", d: "Los supervisores ven y aprueban/rechazan solicitudes de sus subordinados." },
        ]
      },
      "Movimientos": {
        desc: "Registro de ascensos, cambios y transferencias",
        steps: [
          { t: "Registrar movimiento", d: "Registre: ascensos, cambios de departamento, aumentos salariales, transferencias." },
          { t: "Historial", d: "Consulte el historial de movimientos de personal con filtros." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas de documentos de RRHH",
        steps: [
          { t: "Gestionar plantillas", d: "Cree y edite plantillas de documentos de RRHH (cartas, constancias, etc.)." },
        ]
      },
      "Vacaciones": {
        desc: "Solicitud y aprobación de vacaciones con balance automático",
        img: "https://images.pexels.com/photos/12437059/pexels-photo-12437059.jpeg?auto=compress&w=600",
        steps: [
          { t: "Solicitar vacaciones", d: "Seleccione las fechas y envíe su solicitud. El sistema calcula automáticamente los días disponibles." },
          { t: "Aprobar vacaciones", d: "Los supervisores ven las solicitudes pendientes y pueden aprobar/rechazar con comentarios." },
          { t: "Balance", d: "Vea su balance de días: disponibles, tomados, pendientes y programados." },
        ]
      },
      "Reclutamiento": {
        desc: "Publicación de vacantes y pipeline de candidatos",
        img: "https://images.unsplash.com/photo-1765020553552-6286dde23660?w=600&q=60",
        steps: [
          { t: "Publicar vacante", d: "Cree nuevas vacantes con: título, departamento, requisitos y descripción del puesto." },
          { t: "Gestionar candidatos", d: "Registre candidatos, programe entrevistas y dé seguimiento al proceso de selección." },
          { t: "Pipeline", d: "Visualice el estado de cada candidato en el pipeline: Aplicado, Entrevista, Pruebas, Oferta, Contratado." },
        ]
      },
      "Procesos Internos": {
        desc: "Flujos de trabajo: onboarding, offboarding, evaluaciones",
        steps: [
          { t: "Gestionar procesos", d: "Configure y administre flujos de trabajo internos: onboarding, offboarding, evaluaciones." },
          { t: "Crear proceso", d: "Defina los pasos del proceso, responsables y plazos para cada etapa." },
        ]
      },
      "Parámetros": {
        desc: "Configuración de tipos de movimiento y reglas de RRHH",
        steps: [
          { t: "Configurar parámetros", d: "Ajuste los parámetros de RRHH: tipos de movimiento, categorías de solicitudes, reglas de vacaciones." },
        ]
      },
    }
  },
  "Riesgos de Personas": {
    desc: "Gestión de aseguradoras, planes y comparativos de beneficios",
    steps: [
      { t: "Acceder", d: "Navegue a 'Riesgos de Personas' desde el menú lateral." },
    ],
    submenus: {
      "Aseguradoras": {
        desc: "Catálogo de aseguradoras con datos de contacto",
        steps: [
          { t: "Gestionar aseguradoras", d: "Listado de aseguradoras con datos de contacto, planes y condiciones." },
        ]
      },
      "Planes": {
        desc: "Planes de seguros disponibles por aseguradora",
        steps: [
          { t: "Ver planes", d: "Consulte los planes de seguros disponibles por aseguradora con coberturas y tarifas." },
        ]
      },
      "Comparativos": {
        desc: "Comparación lado a lado de planes de seguros",
        steps: [
          { t: "Crear comparativo", d: "Compare planes de diferentes aseguradoras lado a lado para tomar decisiones informadas." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas de documentos de riesgos de personas",
        steps: [
          { t: "Gestionar plantillas", d: "Configure plantillas para documentos de riesgos de personas." },
        ]
      },
    }
  },
  "Informaciones Generales": {
    desc: "Información institucional y datos generales de la empresa",
    steps: [
      { t: "Consultar información", d: "Acceda a datos generales de la organización, directorio y recursos compartidos." },
    ],
    submenus: {}
  },
  "Mensajería": {
    desc: "Gestión de envíos de documentos y paquetes con mensajeros",
    steps: [
      { t: "Acceder", d: "Navegue a 'Mensajería' desde el menú lateral." },
    ],
    tips: ["Las solicitudes urgentes se destacan visualmente.", "Use correspondencia interna para envíos entre sucursales."],
    submenus: {
      "Solicitudes": {
        desc: "Crear y gestionar solicitudes de mensajería",
        img: "https://images.pexels.com/photos/6868457/pexels-photo-6868457.jpeg?auto=compress&w=600",
        steps: [
          { t: "Nueva solicitud", d: "Haga clic en '+ Nueva Solicitud'. Complete: Cliente, Dirección, Tipo de documento, Descripción y asigne un mensajero." },
          { t: "Panel de control", d: "Tarjetas con: Total, Pendientes, En Proceso, Completados, Devueltos y Urgentes." },
          { t: "Gestionar entregas", d: "Use 'Iniciar Grupo' para que el mensajero comience su ruta. 'Completar Grupo' al terminar." },
        ]
      },
      "Mensajeros": {
        desc: "Registro y administración de mensajeros",
        steps: [
          { t: "Gestionar mensajeros", d: "Registre y administre mensajeros disponibles con asignación de zonas." },
        ]
      },
      "Chat": {
        desc: "Comunicación en tiempo real con mensajeros",
        steps: [
          { t: "Chat interno", d: "Comuníquese en tiempo real con los mensajeros sobre entregas activas." },
        ]
      },
      "Tipos de Documento": {
        desc: "Configuración de tipos de documentos de envío",
        steps: [
          { t: "Configurar tipos", d: "Defina los tipos de documentos que se envían (Carta, Paquete, Sobre, etc.)." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas de documentos frecuentes",
        steps: [
          { t: "Gestionar plantillas", d: "Configure plantillas de documentos frecuentes para agilizar las solicitudes." },
        ]
      },
      "Historial": {
        desc: "Búsqueda de entregas pasadas con filtros",
        steps: [
          { t: "Consultar historial", d: "Busque entregas pasadas con filtros por fecha, cliente, mensajero y estado." },
        ]
      },
      "Alertas": {
        desc: "Alertas automáticas para entregas pendientes",
        steps: [
          { t: "Configurar alertas", d: "Defina alertas automáticas para entregas retrasadas o pendientes." },
        ]
      },
      "Aplicación Móvil": {
        desc: "App para que los mensajeros reciban y confirmen rutas",
        steps: [
          { t: "App del mensajero", d: "Los mensajeros usan la app móvil para recibir rutas, confirmar entregas y tomar fotos de entrega." },
        ]
      },
      "Correspondencia Interna": {
        desc: "Envíos internos entre oficinas y sucursales",
        steps: [
          { t: "Envío entre sucursales", d: "Gestione envíos internos entre oficinas y sucursales de la empresa." },
        ]
      },
    }
  },
  "Mesa de Ayuda": {
    desc: "Sistema de tickets para soporte técnico interno",
    steps: [
      { t: "Acceder", d: "Navegue a 'Mesa de Ayuda' desde el menú lateral." },
    ],
    tips: ["Los tickets críticos generan notificaciones inmediatas.", "Use plantillas de respuesta para soluciones frecuentes."],
    submenus: {
      "Tickets": {
        desc: "Bandeja de tickets con filtros y gestión",
        img: "https://images.pexels.com/photos/7709290/pexels-photo-7709290.jpeg?auto=compress&w=600",
        steps: [
          { t: "Ver tickets", d: "Bandeja principal con todos los tickets. Filtre por estado, prioridad y categoría." },
          { t: "Gestionar ticket", d: "Asigne, cambie prioridad, agregue comentarios y resuelva tickets." },
        ]
      },
      "Mis Tickets": {
        desc: "Crear y dar seguimiento a mis tickets",
        steps: [
          { t: "Crear ticket", d: "Haga clic en '+ Nuevo Ticket'. Describa el problema, asigne categoría y prioridad." },
          { t: "Seguimiento", d: "Vea el estado actualizado de sus tickets y agregue comentarios adicionales." },
        ]
      },
      "Categorías": {
        desc: "Definición de categorías de tickets",
        steps: [
          { t: "Configurar categorías", d: "Defina categorías de tickets: Hardware, Software, Red, Accesos, etc." },
        ]
      },
      "Prioridades": {
        desc: "Niveles de prioridad con SLAs",
        steps: [
          { t: "Configurar prioridades", d: "Defina niveles con colores y SLAs: Baja, Normal, Alta, Crítica." },
        ]
      },
      "Plantillas": {
        desc: "Respuestas predefinidas para problemas frecuentes",
        steps: [
          { t: "Plantillas de respuesta", d: "Cree respuestas predefinidas para problemas frecuentes." },
        ]
      },
    }
  },
  "WhatsApp": {
    desc: "Bandeja de conversaciones de WhatsApp Business integrada",
    steps: [
      { t: "Acceder", d: "Navegue a 'WhatsApp' desde el menú lateral." },
    ],
    tips: ["Las conversaciones se actualizan en tiempo real.", "Configure el chatbot para consultas básicas automáticas."],
    submenus: {
      "Bandeja de Entrada": {
        desc: "Todas las conversaciones en una bandeja unificada",
        img: "https://images.unsplash.com/photo-1644035772775-8e26685ad2f0?w=600&q=60",
        steps: [
          { t: "Ver conversaciones", d: "Todas las conversaciones de WhatsApp en la bandeja unificada. No leídas se resaltan." },
          { t: "Responder", d: "Seleccione una conversación para ver el historial y responder con texto, imágenes o documentos." },
        ]
      },
      "Conversaciones": {
        desc: "Historial completo de conversaciones con buscador",
        steps: [
          { t: "Historial completo", d: "Consulte el historial de todas las conversaciones con buscador y filtros." },
        ]
      },
      "Asignaciones": {
        desc: "Asignar conversaciones a agentes específicos",
        steps: [
          { t: "Asignar agente", d: "Asigne conversaciones a agentes específicos para mejor seguimiento." },
        ]
      },
      "Chatbot IA": {
        desc: "Respuestas automáticas del chatbot inteligente",
        steps: [
          { t: "Configurar chatbot", d: "Configure respuestas automáticas del chatbot para atención fuera de horario o FAQ." },
        ]
      },
      "Respuestas Automáticas": {
        desc: "Respuestas rápidas para preguntas frecuentes",
        steps: [
          { t: "Respuestas rápidas", d: "Configure respuestas predefinidas para preguntas frecuentes." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas aprobadas para mensajes proactivos",
        steps: [
          { t: "Plantillas WhatsApp", d: "Use plantillas aprobadas para mensajes proactivos (recordatorios, notificaciones)." },
        ]
      },
      "Contactos WhatsApp": {
        desc: "Directorio de contactos con etiquetas",
        steps: [
          { t: "Directorio", d: "Gestione el directorio de contactos de WhatsApp con etiquetas y segmentación." },
        ]
      },
      "Configuración": {
        desc: "Horarios, mensajes de bienvenida y reglas",
        steps: [
          { t: "Ajustes WhatsApp", d: "Configure: horarios de atención, mensajes de bienvenida, agentes y reglas de asignación." },
        ]
      },
    }
  },
  "Finanzas y Contabilidad": {
    desc: "Inventario, mobiliario, caja chica, cuentas por pagar y agenda",
    steps: [
      { t: "Acceder", d: "Navegue a 'Finanzas y Contabilidad' para ver todos los submódulos financieros." },
    ],
    submenus: {
      "Dashboard Materiales": {
        desc: "Valor total del inventario y movimientos recientes",
        img: "https://images.pexels.com/photos/6779567/pexels-photo-6779567.jpeg?auto=compress&w=600",
        steps: [
          { t: "Vista general", d: "Valor total del inventario, artículos en stock, bajo mínimo y movimientos recientes." },
        ]
      },
      "Artículos": {
        desc: "Catálogo de artículos de inventario",
        steps: [
          { t: "Gestionar artículos", d: "Catálogo con: nombre, código, categoría, unidad de medida, stock mínimo, ubicación." },
          { t: "Agregar artículo", d: "Haga clic en '+ Nuevo Artículo' y complete los datos." },
        ]
      },
      "Entradas": {
        desc: "Registro de entradas de mercancía de proveedores",
        steps: [
          { t: "Registrar entrada", d: "Registre entradas de mercancía: proveedor, factura, artículos y cantidades. Stock se actualiza automáticamente." },
        ]
      },
      "Salidas": {
        desc: "Despacho de mercancía a departamentos",
        steps: [
          { t: "Registrar salida", d: "Registre despachos: departamento solicitante, artículos y cantidades. Se descuenta del stock." },
        ]
      },
      "Movimientos": {
        desc: "Historial completo de entradas, salidas y ajustes",
        steps: [
          { t: "Historial", d: "Historial completo de movimientos (entradas, salidas, ajustes) con filtros." },
        ]
      },
      "Categorías Materiales": {
        desc: "Organización de artículos por categorías",
        steps: [
          { t: "Organizar", d: "Organice los artículos en categorías para mejor gestión." },
        ]
      },
      "Dashboard Mobiliario": {
        desc: "Total de activos fijos, valor y mantenimientos",
        steps: [
          { t: "Vista de activos", d: "Total de activos, valor total, asignados y próximos mantenimientos." },
        ]
      },
      "Activos": {
        desc: "Registro de activos fijos de la empresa",
        steps: [
          { t: "Registrar activos", d: "Registre activos fijos: nombre, código, categoría, ubicación, valor, estado." },
        ]
      },
      "Asignaciones": {
        desc: "Asignación de activos a empleados",
        steps: [
          { t: "Asignar activos", d: "Asigne activos a empleados o departamentos con registro de entrega." },
        ]
      },
      "Mantenimiento": {
        desc: "Mantenimientos preventivos y correctivos",
        steps: [
          { t: "Programar", d: "Programe y registre mantenimientos preventivos y correctivos." },
        ]
      },
      "Bajas": {
        desc: "Registro de baja de activos por deterioro o pérdida",
        steps: [
          { t: "Registrar baja", d: "Registre baja de activos por deterioro, pérdida o venta." },
        ]
      },
      "Categorías Mobiliario": {
        desc: "Categorías de activos fijos",
        steps: [
          { t: "Configurar categorías", d: "Defina las categorías de activos fijos." },
        ]
      },
      "Ubicaciones": {
        desc: "Ubicaciones físicas de almacenamiento",
        steps: [
          { t: "Gestionar ubicaciones", d: "Defina las ubicaciones físicas donde se almacenan los activos." },
        ]
      },
      "Caja Chica": {
        desc: "Control de gastos menores y fondo disponible",
        steps: [
          { t: "Registrar gasto", d: "Registre gastos menores: concepto, monto, fecha, categoría, comprobante." },
          { t: "Balance", d: "Vea balance actual, gastos acumulados y fondo disponible." },
          { t: "Reposición", d: "Solicite reposición cuando el balance esté bajo." },
        ]
      },
      "Configuración Caja": {
        desc: "Monto inicial, categorías y aprobadores",
        steps: [
          { t: "Ajustes", d: "Configure monto inicial del fondo, categorías de gasto y aprobadores." },
        ]
      },
      "Cuentas por Pagar": {
        desc: "Gestión de facturas pendientes y pagos",
        img: "https://images.pexels.com/photos/6779567/pexels-photo-6779567.jpeg?auto=compress&w=600",
        steps: [
          { t: "Nueva factura", d: "Ingrese: Número, NCF, Proveedor, Categoría, Monto, Moneda, Vencimiento." },
          { t: "Panel de control", d: "Total Pendiente, Monto Vencido, Próximas a vencer, Pagado este mes." },
          { t: "Registrar pago", d: "Registre abonos parciales o pago total. Estado se actualiza automáticamente." },
        ]
      },
      "Proveedores": {
        desc: "Catálogo de proveedores con datos de contacto",
        steps: [
          { t: "Catálogo", d: "Gestione proveedores con datos de contacto, RNC y categorización." },
        ]
      },
      "Historial": {
        desc: "Historial completo de pagos realizados",
        steps: [
          { t: "Consultar pagos", d: "Historial completo de pagos realizados con filtros por fecha y proveedor." },
        ]
      },
      "Alertas de Pagos": {
        desc: "Alertas para facturas próximas a vencer",
        steps: [
          { t: "Configurar alertas", d: "Alertas automáticas para facturas próximas a vencer o vencidas." },
        ]
      },
      "Control Medicamentos": {
        desc: "Inventario de medicamentos con vencimientos",
        steps: [
          { t: "Gestionar medicamentos", d: "Control de inventario de medicamentos con fechas de vencimiento y stock mínimo." },
        ]
      },
      "Agenda de Trabajo": {
        desc: "Organización de actividades diarias y semanales",
        steps: [
          { t: "Programar actividades", d: "Organice y programe actividades de trabajo diarias y semanales." },
        ]
      },
    }
  },
  "Control Visitantes": {
    desc: "Registro y seguimiento de visitantes en las instalaciones",
    steps: [
      { t: "Acceder", d: "Navegue a 'Control Visitantes' desde el menú lateral." },
    ],
    tips: ["Los visitantes con tiempo excedido se resaltan automáticamente.", "Busque por nombre, cédula o empresa."],
    submenus: {
      "Registrar Visita": {
        desc: "Registro de entrada y salida de visitantes",
        img: "https://images.unsplash.com/photo-1759143545924-beb85b33c0f1?w=600&q=60",
        steps: [
          { t: "Nueva visita", d: "Ingrese: nombre del visitante, cédula, empresa, persona a visitar y motivo." },
          { t: "Registrar salida", d: "Cuando el visitante se retira, haga clic en 'Salida' para registrar la hora." },
        ]
      },
      "Historial": {
        desc: "Historial de visitas con filtros",
        steps: [
          { t: "Consultar visitas", d: "Historial completo con filtros por fecha, visitante y empresa." },
        ]
      },
      "Tipos de Visita": {
        desc: "Definición de tipos de visita",
        steps: [
          { t: "Configurar tipos", d: "Defina tipos de visita: Reunión, Entrevista, Entrega, Personal, etc." },
        ]
      },
      "Reportes": {
        desc: "Reportes de visitas por período",
        steps: [
          { t: "Generar reportes", d: "Reportes de visitas por período, empresa o persona visitada." },
        ]
      },
      "Alertas": {
        desc: "Alertas para visitantes con tiempo excedido",
        steps: [
          { t: "Configurar alertas", d: "Alertas automáticas para visitantes con tiempo excedido en las instalaciones." },
        ]
      },
    }
  },
  "Requisiciones": {
    desc: "Solicitudes de compra de materiales con flujo de aprobación",
    steps: [
      { t: "Acceder", d: "Navegue a 'Requisiciones' desde el menú lateral." },
    ],
    tips: ["Los artículos se toman del catálogo de inventario.", "Las aprobaciones generan notificaciones automáticas."],
    submenus: {
      "Mis Requisiciones": {
        desc: "Listado de sus requisiciones con estado",
        img: "https://images.unsplash.com/photo-1579808352667-5db8c02598ce?w=600&q=60",
        steps: [
          { t: "Ver mis solicitudes", d: "Listado de sus requisiciones con estado: Pendiente, Aprobada, Parcial, Entregada, Rechazada." },
        ]
      },
      "Nueva Requisición": {
        desc: "Crear nueva solicitud de compra",
        steps: [
          { t: "Crear requisición", d: "Haga clic en '+ Nueva Requisición'. Complete: Justificación, Fecha requerida y agregue artículos con cantidad." },
        ]
      },
      "Por Aprobar": {
        desc: "Aprobar o rechazar requisiciones pendientes",
        steps: [
          { t: "Aprobar requisiciones", d: "Los aprobadores ven requisiciones pendientes. Pueden aprobar, rechazar o solicitar modificaciones." },
        ]
      },
      "Historial": {
        desc: "Historial de requisiciones con filtros",
        steps: [
          { t: "Consultar historial", d: "Historial completo con filtros por fecha, estado y solicitante." },
        ]
      },
    }
  },
  "Artículos Marketing": {
    desc: "Gestión de artículos promocionales y material de marketing",
    steps: [
      { t: "Gestionar artículos", d: "Administre el inventario de artículos promocionales y material de marketing." },
    ],
    submenus: {}
  },
  "Modulo de encuesta": {
    desc: "Creación y gestión de encuestas de satisfacción",
    steps: [
      { t: "Acceder", d: "Navegue a 'Módulo de Encuesta' desde el menú lateral." },
    ],
    tips: ["Las encuestas generan un enlace público compartible.", "Use análisis IA para interpretar resultados."],
    submenus: {
      "Portal Público": {
        desc: "Vista pública de encuestas activas",
        steps: [
          { t: "Portal de encuestas", d: "Vista pública de encuestas activas para que los participantes puedan responder." },
        ]
      },
      "Plantillas": {
        desc: "Plantillas de encuestas reutilizables",
        steps: [
          { t: "Crear plantilla", d: "Defina plantillas de encuestas reutilizables con preguntas predefinidas." },
        ]
      },
      "Encuestas": {
        desc: "Crear y gestionar encuestas con múltiples tipos de pregunta",
        img: "https://images.unsplash.com/photo-1587882242636-596ab6349e69?w=600&q=60",
        steps: [
          { t: "Crear encuesta", d: "Configure encuestas con preguntas: opción múltiple, escala de satisfacción, texto libre." },
          { t: "Gestionar encuestas", d: "Active, desactive o edite encuestas existentes." },
        ]
      },
      "Resultados": {
        desc: "Gráficos y análisis de respuestas",
        steps: [
          { t: "Ver resultados", d: "Gráficos, porcentajes y análisis detallado de cada pregunta." },
        ]
      },
      "Programador": {
        desc: "Programar envíos automáticos de encuestas",
        steps: [
          { t: "Programar envíos", d: "Programe encuestas para enviarse automáticamente en fechas específicas o periódicamente." },
        ]
      },
    }
  },
  "Estadísticas": {
    desc: "Reportes estadísticos y métricas de rendimiento",
    steps: [
      { t: "Ver estadísticas", d: "Acceda a reportes estadísticos generales de la plataforma." },
    ],
    submenus: {}
  },
  "Convertidor de Documentos": {
    desc: "Herramienta para convertir documentos entre formatos",
    steps: [
      { t: "Convertir documento", d: "Suba un documento y seleccione el formato de salida deseado para la conversión." },
    ],
    submenus: {}
  },
  "Publicidades": {
    desc: "Gestión de material publicitario y campañas",
    steps: [
      { t: "Gestionar publicidades", d: "Administre el material publicitario y las campañas activas." },
    ],
    submenus: {}
  },
  "Capacitaciones": {
    desc: "Plataforma de aprendizaje y capacitación interna",
    steps: [
      { t: "Acceder", d: "Navegue a 'Capacitaciones' para ver cursos y progreso." },
    ],
    submenus: {
      "Cursos": {
        desc: "Catálogo de cursos disponibles",
        img: "https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?w=600&q=60",
        steps: [
          { t: "Ver cursos", d: "Listado de cursos disponibles con descripción, duración y estado." },
        ]
      },
      "Mi Progreso": {
        desc: "Avance personal en cursos asignados",
        steps: [
          { t: "Progreso personal", d: "Vea su avance en cursos asignados, completados y pendientes." },
        ]
      },
      "Dashboard Admin": {
        desc: "Administración y estadísticas de cursos",
        steps: [
          { t: "Administrar cursos", d: "Cree, edite y asigne cursos. Vea estadísticas de participación." },
        ]
      },
      "Certificados": {
        desc: "Descarga de certificados de cursos completados",
        steps: [
          { t: "Mis certificados", d: "Descargue certificados de cursos completados." },
        ]
      },
    }
  },
  "Exámenes": {
    desc: "Sistema de evaluaciones y exámenes internos",
    steps: [
      { t: "Acceder", d: "Navegue a 'Exámenes' para ver evaluaciones disponibles." },
    ],
    submenus: {
      "Mis Exámenes": {
        desc: "Evaluaciones asignadas y pendientes",
        img: "https://images.unsplash.com/photo-1640163561346-7778a2edf353?w=600&q=60",
        steps: [
          { t: "Tomar examen", d: "Vea los exámenes asignados y complete las evaluaciones pendientes." },
        ]
      },
      "Banco de Preguntas": {
        desc: "Gestión de preguntas por tema y dificultad",
        steps: [
          { t: "Gestionar preguntas", d: "Cree y organice preguntas por tema y dificultad." },
        ]
      },
      "Resultados": {
        desc: "Calificaciones y estadísticas de exámenes",
        steps: [
          { t: "Ver resultados", d: "Consulte resultados de exámenes con calificaciones y estadísticas." },
        ]
      },
    }
  },
  "Reportes": {
    desc: "Generación de reportes predefinidos y personalizados",
    steps: [
      { t: "Acceder", d: "Navegue a 'Reportes' para generar informes." },
    ],
    submenus: {
      "Reportes Predefinidos": {
        desc: "Reportes listos para descargar",
        steps: [
          { t: "Generar reporte", d: "Seleccione un reporte predefinido, configure los parámetros y descargue." },
        ]
      },
      "Reportes Personalizados": {
        desc: "Diseño de reportes con campos y filtros",
        steps: [
          { t: "Crear reporte", d: "Diseñe reportes personalizados seleccionando campos, filtros y formato de salida." },
        ]
      },
    }
  },
  "Configuración": {
    desc: "Ajustes generales del sistema, usuarios, permisos y personalización",
    steps: [
      { t: "Acceder", d: "Navegue a 'Configuración' desde el menú lateral." },
    ],
    tips: ["Los cambios de permisos se aplican inmediatamente.", "Use la auditoría para investigar acciones."],
    submenus: {
      "Usuarios": {
        desc: "Crear, editar y gestionar cuentas de usuario",
        img: "https://images.pexels.com/photos/3785930/pexels-photo-3785930.jpeg?auto=compress&w=600",
        steps: [
          { t: "Gestionar usuarios", d: "Cree nuevos usuarios, edite roles, active/desactive cuentas." },
        ]
      },
      "Departamentos": {
        desc: "Estructura organizacional con jerarquías",
        steps: [
          { t: "Estructura organizacional", d: "Administre departamentos con jerarquías y responsables." },
        ]
      },
      "Permisos": {
        desc: "Control de acceso por roles y módulos",
        steps: [
          { t: "Configurar permisos", d: "Defina permisos de acceso por rol: qué módulos y acciones puede realizar cada tipo de usuario." },
        ]
      },
      "Administrar Menús": {
        desc: "Personalización del menú lateral",
        steps: [
          { t: "Personalizar menús", d: "Agregue, oculte o reordene opciones del menú lateral." },
        ]
      },
      "Campos Personalizados": {
        desc: "Campos adicionales para formularios",
        steps: [
          { t: "Crear campos", d: "Agregue campos personalizados a formularios existentes según sus necesidades." },
        ]
      },
      "Campos Obligatorios": {
        desc: "Configuración de obligatoriedad por formulario",
        steps: [
          { t: "Configurar obligatoriedad", d: "Defina qué campos son obligatorios en cada formulario." },
        ]
      },
      "Clientes Mensajería": {
        desc: "Catálogo de clientes para mensajería",
        steps: [
          { t: "Gestionar clientes", d: "Administre el catálogo de clientes para el módulo de mensajería." },
        ]
      },
      "Reglas de Alertas": {
        desc: "Reglas automáticas para eventos del sistema",
        steps: [
          { t: "Configurar reglas", d: "Defina reglas automáticas para generar alertas en eventos específicos." },
        ]
      },
      "Ejecutor de Alertas": {
        desc: "Monitor de alertas generadas",
        steps: [
          { t: "Monitor de alertas", d: "Vea y gestione las alertas generadas por las reglas configuradas." },
        ]
      },
      "Notificaciones Email": {
        desc: "Destinatarios y frecuencia de correos",
        steps: [
          { t: "Configurar email", d: "Ajuste las notificaciones por correo electrónico: destinatarios y frecuencia." },
        ]
      },
      "Notificaciones Push": {
        desc: "Notificaciones push del navegador",
        steps: [
          { t: "Configurar push", d: "Active y configure notificaciones push del navegador." },
        ]
      },
      "Personalización": {
        desc: "Colores, logo y apariencia de la plataforma",
        steps: [
          { t: "Ajustar apariencia", d: "Personalice colores, logo y apariencia general de la plataforma." },
        ]
      },
      "Aprendizaje IA": {
        desc: "Entrenamiento de modelos de IA",
        steps: [
          { t: "Entrenar IA", d: "Configure y entrene los modelos de IA con datos específicos de su organización." },
        ]
      },
      "Limpieza Base de Datos": {
        desc: "Mantenimiento y optimización",
        steps: [
          { t: "Mantenimiento", d: "Limpie datos obsoletos y optimice el rendimiento de la base de datos." },
        ]
      },
      "Copias de Seguridad": {
        desc: "Crear y restaurar backups",
        steps: [
          { t: "Gestionar backups", d: "Cree y restaure copias de seguridad de la base de datos." },
        ]
      },
      "Plantillas de Correos": {
        desc: "Edición de plantillas de notificación",
        steps: [
          { t: "Configurar plantillas", d: "Edite las plantillas de correo para cada tipo de notificación del sistema." },
        ]
      },
      "Reglas de Email": {
        desc: "Eventos que disparan correos automáticos",
        steps: [
          { t: "Configurar reglas", d: "Defina qué eventos disparan correos y a quiénes se envían." },
        ]
      },
      "Monitor de Correos": {
        desc: "Estado de correos enviados",
        steps: [
          { t: "Ver envíos", d: "Monitoree los correos enviados: estado, destinatario, fecha y contenido." },
        ]
      },
      "Firma Digital": {
        desc: "Firma digital institucional para documentos",
        steps: [
          { t: "Configurar firma", d: "Configure la firma digital institucional para documentos generados." },
        ]
      },
      "Operaciones por Usuario": {
        desc: "Auditoría de operaciones por usuario",
        steps: [
          { t: "Auditar operaciones", d: "Consulte las operaciones realizadas por cada usuario en el sistema." },
        ]
      },
      "Datos Llamadas Telefónicas": {
        desc: "Registros de llamadas telefónicas",
        steps: [
          { t: "Ver registros", d: "Consulte los datos de llamadas telefónicas registradas." },
        ]
      },
      "Accesos Portales": {
        desc: "Accesos a portales externos",
        steps: [
          { t: "Gestionar accesos", d: "Configure y monitoree los accesos a portales externos." },
        ]
      },
    }
  },
};

// ===== DRAG STATE (module-level to avoid closure issues) =====
let _fabDragActive = false;
let _fabDragMoved = false;
let _fabBlockClick = false;
let _fabOffX = 0;
let _fabOffY = 0;

// ===== COMPONENT =====
export default function ManualFloatingButton() {
  const { user, sidebarMenus } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const STORAGE_KEY = "manual_fab_position";
  const btnRef = useRef(null);
  const posRef = useRef(null);

  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        const pos = { x: Math.min(p.x, window.innerWidth - 56), y: Math.min(p.y, window.innerHeight - 56) };
        posRef.current = pos;
        return pos;
      }
    } catch {}
    const def = { x: window.innerWidth - 80, y: window.innerHeight - 80 };
    posRef.current = def;
    return def;
  });

  useEffect(() => {
    const handleMove = (e) => {
      if (!_fabDragActive) return;
      e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const nx = Math.max(0, Math.min(cx - _fabOffX, window.innerWidth - 56));
      const ny = Math.max(0, Math.min(cy - _fabOffY, window.innerHeight - 56));
      if (!_fabDragMoved) {
        const p = posRef.current || { x: 0, y: 0 };
        if (Math.abs(nx - p.x) + Math.abs(ny - p.y) > 5) {
          _fabDragMoved = true;
          _fabBlockClick = true;
        }
      }
      posRef.current = { x: nx, y: ny };
      const el = document.querySelector('[data-testid="manual-floating-btn"]');
      if (el) { el.style.left = nx + "px"; el.style.top = ny + "px"; }
    };

    const handleUp = (e) => {
      if (!_fabDragActive) return;
      _fabDragActive = false;
      if (_fabDragMoved && posRef.current) {
        _fabBlockClick = true;
        setPosition({ ...posRef.current });
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current)); } catch {}
      }
      _fabDragMoved = false;
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const c = {
          x: Math.max(0, Math.min(prev.x, window.innerWidth - 56)),
          y: Math.max(0, Math.min(prev.y, window.innerHeight - 56)),
        };
        posRef.current = c;
        return c;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Permission check - same logic as Sidebar's hasAccess
  const hasAccess = (permissionKey) => {
    const userRole = (user?.role || "").toLowerCase();
    if (userRole === "admin" || userRole === "administrador") return true;
    if (!user?.menu_permissions) return false;
    const perm = user.menu_permissions[permissionKey];
    if (perm?.access === false) return false;
    if (perm?.access === true) return true;
    return false;
  };

  // Build filtered manual from user's permitted menus
  const userManual = useMemo(() => {
    if (!sidebarMenus || sidebarMenus.length === 0) return [];
    const userRole = (user?.role || "").toLowerCase();
    const isAdmin = userRole === "admin" || userRole === "administrador";

    return sidebarMenus
      .filter(menu => {
        if (isAdmin) return true;
        // Check if user has access to this menu (or any of its submenus)
        if (hasAccess(menu.id)) return true;
        const accessibleSubs = (menu.submenus || []).filter(sub => hasAccess(sub.id));
        return accessibleSubs.length > 0;
      })
      .map(menu => {
        const content = manualDB[menu.label];
        if (!content) return null;
        // Filter submenus by permission too
        const permittedSubs = (menu.submenus || []).filter(sub => {
          if (isAdmin) return true;
          return hasAccess(sub.id) || hasAccess(sub.permission || sub.id);
        });
        const subs = permittedSubs.map(sub => {
            const subData = content.submenus?.[sub.label];
            if (!subData) return { label: sub.label, desc: `Acceda a ${menu.label} > ${sub.label}`, steps: [{ t: sub.label, d: `Acceda a ${menu.label} > ${sub.label} desde el menú lateral.` }] };
            return { label: sub.label, desc: subData.desc || "", img: subData.img, steps: subData.steps || [] };
          });
        return {
          id: menu.label,
          label: menu.label,
          icon: menu.icon,
          img: moduleImages[menu.label],
          desc: content.desc,
          steps: content.steps,
          tips: content.tips || [],
          submenus: subs,
        };
      })
      .filter(Boolean);
  }, [sidebarMenus, user]);

  const filtered = searchTerm
    ? userManual.filter(m =>
        m.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.submenus.some(s =>
          s.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.desc?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : userManual;

  const currentModule = activeModule ? userManual.find(m => m.id === activeModule) : null;
  const currentSubmenu = activeSubmenu && currentModule ? currentModule.submenus.find(s => s.label === activeSubmenu) : null;

  const close = () => { setIsOpen(false); setActiveModule(null); setActiveSubmenu(null); setSearchTerm(""); };
  const goBack = () => {
    if (activeSubmenu) { setActiveSubmenu(null); }
    else if (activeModule) { setActiveModule(null); }
  };

  // Determine breadcrumb / title
  let headerTitle = "Manual de Usuario";
  let headerSub = `${userManual.length} módulos disponibles`;
  if (activeSubmenu && currentModule) {
    headerTitle = activeSubmenu;
    headerSub = currentModule.label;
  } else if (activeModule && currentModule) {
    headerTitle = currentModule.label;
    headerSub = currentModule.desc;
  }

  if (!user) return null;

  return (
    <>
      {/* Floating button - Draggable */}
      <button
        ref={btnRef}
        onMouseDown={(e) => {
          if (isOpen) return;
          const p = posRef.current || position;
          _fabDragActive = true;
          _fabDragMoved = false;
          _fabOffX = e.clientX - p.x;
          _fabOffY = e.clientY - p.y;
          e.preventDefault();
        }}
        onTouchStart={(e) => {
          if (isOpen) return;
          const t = e.touches[0];
          const p = posRef.current || position;
          _fabDragActive = true;
          _fabDragMoved = false;
          _fabOffX = t.clientX - p.x;
          _fabOffY = t.clientY - p.y;
          e.preventDefault();
        }}
        onClick={() => {
          if (_fabBlockClick) {
            _fabBlockClick = false;
            return;
          }
          if (!isOpen) setIsOpen(true);
        }}
        style={{
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: 45,
          touchAction: "none",
          cursor: "grab",
        }}
        className="w-14 h-14 rounded-full bg-[#193966] text-white shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center select-none"
        data-testid="manual-floating-btn"
        title="Manual de Usuario (arrastra para mover)"
      >
        <BookOpen className="w-6 h-6 pointer-events-none" />
      </button>

      {/* Overlay + Panel */}
      {isOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50 transition-opacity" onClick={close} />
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg z-50 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" data-testid="manual-panel">
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                {(activeModule || activeSubmenu) && (
                  <button onClick={goBack} className="p-1 rounded-lg hover:bg-slate-200 transition-colors mr-1" data-testid="manual-back-btn">
                    <ArrowLeft className="w-4 h-4 text-slate-600" />
                  </button>
                )}
                <div className="w-8 h-8 rounded-lg bg-[#193966] flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-slate-800 truncate">{headerTitle}</h2>
                  <p className="text-xs text-slate-500 truncate">{headerSub}</p>
                </div>
              </div>
              <button onClick={close} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors" data-testid="manual-close-btn">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* ====== LEVEL 1: MODULE LIST ====== */}
              {!activeModule && (
                <div className="p-4 space-y-2">
                  <div className="relative mb-3">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      placeholder="Buscar en el manual..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 h-9 text-sm bg-slate-50 border-slate-200"
                      data-testid="manual-float-search"
                    />
                  </div>

                  {filtered.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">No se encontraron resultados</p>
                  ) : (
                    filtered.map(mod => (
                      <button
                        key={mod.id}
                        onClick={() => { setActiveModule(mod.id); setSearchTerm(""); }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`manual-nav-${mod.id.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {mod.img ? (
                          <img src={mod.img} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-[#193966]/10 flex items-center justify-center shrink-0">
                            <BookOpen className="w-4 h-4 text-[#193966]" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{mod.label}</p>
                          <p className="text-xs text-slate-500 truncate">{mod.desc}</p>
                          {mod.submenus.length > 0 && (
                            <p className="text-xs text-slate-400 mt-0.5">{mod.submenus.length} opciones</p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* ====== LEVEL 2: MODULE DETAIL → SUBMENU LIST ====== */}
              {activeModule && !activeSubmenu && currentModule && (
                <div className="space-y-0">
                  {/* Module hero image */}
                  {currentModule.img && (
                    <div className="relative h-40 overflow-hidden">
                      <img src={currentModule.img} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <p className="text-white text-base font-bold">{currentModule.label}</p>
                        <p className="text-white/80 text-xs">{currentModule.desc}</p>
                      </div>
                    </div>
                  )}

                  <div className="p-4 space-y-3">
                    {/* General module steps (intro) */}
                    {currentModule.steps?.length > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                        <p className="text-xs font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                          <HelpCircle className="w-3.5 h-3.5" /> Descripción general
                        </p>
                        {currentModule.steps.map((step, i) => (
                          <p key={i} className="text-xs text-blue-700 leading-relaxed mb-1">{step.d}</p>
                        ))}
                      </div>
                    )}

                    {/* Submenus as clickable cards */}
                    {currentModule.submenus.length > 0 && (
                      <>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                          Opciones del módulo ({currentModule.submenus.length})
                        </p>
                        {currentModule.submenus.map((sub) => (
                          <button
                            key={sub.label}
                            onClick={() => setActiveSubmenu(sub.label)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-[#193966]/30 hover:bg-[#193966]/5 transition-colors text-left group"
                            data-testid={`manual-sub-${sub.label.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {sub.img ? (
                              <img src={sub.img} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-[#193966]/10 flex items-center justify-center shrink-0 transition-colors">
                                <BookOpen className="w-4 h-4 text-slate-500 group-hover:text-[#193966] transition-colors" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 group-hover:text-[#193966] truncate transition-colors">{sub.label}</p>
                              <p className="text-xs text-slate-500 truncate">{sub.desc || `${sub.steps.length} paso(s)`}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#193966] shrink-0 transition-colors" />
                          </button>
                        ))}
                      </>
                    )}

                    {/* No submenus - show main steps as the content */}
                    {currentModule.submenus.length === 0 && currentModule.steps?.length > 0 && (
                      <div className="space-y-2 mt-2">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">Instrucciones</p>
                        {currentModule.steps.map((step, i) => (
                          <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="w-6 h-6 rounded-md bg-[#193966] flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5">
                              {i + 1}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-700">{step.t}</p>
                              <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{step.d}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Tips */}
                    {currentModule.tips?.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5" /> Consejos útiles
                        </p>
                        {currentModule.tips.map((tip, i) => (
                          <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5 mb-1">
                            <CheckCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                            {tip}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ====== LEVEL 3: SUBMENU DETAIL (INSTRUCTIONS) ====== */}
              {activeSubmenu && currentSubmenu && currentModule && (
                <div className="space-y-0">
                  {/* Submenu hero image */}
                  {(currentSubmenu.img || currentModule.img) && (
                    <div className="relative h-36 overflow-hidden">
                      <img src={currentSubmenu.img || currentModule.img} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <p className="text-white/70 text-[10px] font-medium uppercase tracking-wide">{currentModule.label}</p>
                        <p className="text-white text-base font-bold">{currentSubmenu.label}</p>
                      </div>
                    </div>
                  )}

                  <div className="p-4 space-y-3">
                    {/* Description */}
                    {currentSubmenu.desc && (
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-xl">
                        <p className="text-xs text-blue-700 leading-relaxed">{currentSubmenu.desc}</p>
                      </div>
                    )}

                    {/* Steps */}
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1">
                      Pasos a seguir ({currentSubmenu.steps.length})
                    </p>
                    {currentSubmenu.steps.map((step, i) => (
                      <div key={i} className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="w-7 h-7 rounded-lg bg-[#193966] flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5">
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-700">{step.t}</p>
                          <p className="text-xs text-slate-600 leading-relaxed mt-1">{step.d}</p>
                          {step.img && (
                            <img src={step.img} alt="" className="mt-2 rounded-lg border border-slate-200 w-full max-h-48 object-cover" />
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Module tips shown here too */}
                    {currentModule.tips?.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl mt-2">
                        <p className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1.5">
                          <Star className="w-3.5 h-3.5" /> Consejos
                        </p>
                        {currentModule.tips.map((tip, i) => (
                          <p key={i} className="text-xs text-amber-700 flex items-start gap-1.5 mb-1">
                            <CheckCircle className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                            {tip}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 shrink-0">
              <p className="text-xs text-slate-400 text-center">
                Mostrando {userManual.length} módulos según sus permisos
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
